import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeTone } from '@artosera/ui';
import { ETATS_CREANCE } from '@artosera/shared';
import type { CreanceSaisie, EtatCreance } from '../../types.js';
import { CelluleMontant, EtatOnglet, formaterJour, useLangue, useMessageErreur } from './commun.js';
import { TableauSaisie, type ChampSaisie } from './TableauSaisie.js';
import { useSaisies } from './useSaisies.js';

const TON_ETAT: Record<EtatCreance, BadgeTone> = {
  due: 'reserved',
  partielle: 'accent',
  reglee: 'available',
  annulee: 'withdrawn',
};

/**
 * Les sommes dues (exploitation, direction) : ce que chaque client doit
 * encore. L'API refuse « réglée » tant que la somme n'est pas soldée
 * (`creance_non_soldee`) : le motif s'affiche sur la ligne.
 */
export function CreancesTab({ enEcriture }: { enEcriture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur('creances');
  const { lignes, chargement, enErreur, creer, modifier, supprimer } = useSaisies('creances');

  const champs: ChampSaisie[] = [
    {
      cle: 'debiteur',
      titre: t('finances.creances.champs.debiteur'),
      type: 'texte',
      requis: true,
      longueurMax: 160,
    },
    {
      cle: 'libelle',
      titre: t('finances.creances.champs.libelle'),
      type: 'texte',
      requis: true,
      longueurMax: 160,
    },
    {
      cle: 'echeanceLe',
      titre: t('finances.creances.champs.echeance'),
      type: 'date',
      requis: true,
    },
    { cle: 'montantCentimes', titre: t('finances.creances.champs.montant'), type: 'montant' },
    {
      cle: 'encaisseCentimes',
      titre: t('finances.creances.champs.encaisse'),
      type: 'montant',
      videVaut: 0,
    },
    {
      cle: 'etat',
      titre: t('finances.creances.champs.etat'),
      type: 'choix',
      requis: true,
      initiale: 'due',
      options: ETATS_CREANCE.map((etat) => ({
        value: etat,
        label: t(`finances.creances.etat.${etat}`),
      })),
    },
    { cle: 'regleeLe', titre: t('finances.creances.champs.reglee'), type: 'date' },
  ];

  if (chargement || enErreur) return <EtatOnglet chargement={chargement} enErreur={enErreur} />;
  return (
    <TableauSaisie<CreanceSaisie>
      legende={t('finances.onglets.creances')}
      introduction={t('finances.creances.texte')}
      lignes={lignes}
      colonnes={[
        {
          cle: 'echeance',
          titre: t('finances.creances.colonnes.echeance'),
          rendu: (ligne) => formaterJour(ligne.echeanceLe, langue),
        },
        {
          cle: 'debiteur',
          titre: t('finances.creances.colonnes.debiteur'),
          rendu: (ligne) => ligne.debiteur,
        },
        {
          cle: 'libelle',
          titre: t('finances.creances.colonnes.libelle'),
          rendu: (ligne) => ligne.libelle,
        },
        {
          cle: 'montant',
          titre: t('finances.creances.colonnes.montant'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.montantCentimes} />,
        },
        {
          cle: 'encaisse',
          titre: t('finances.creances.colonnes.encaisse'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.encaisseCentimes} />,
        },
        {
          cle: 'etat',
          titre: t('finances.creances.colonnes.etat'),
          rendu: (ligne) => (
            <Badge tone={TON_ETAT[ligne.etat]}>{t(`finances.creances.etat.${ligne.etat}`)}</Badge>
          ),
        },
      ]}
      champs={champs}
      valeursDe={(ligne) => ({
        debiteur: ligne.debiteur,
        libelle: ligne.libelle,
        echeanceLe: ligne.echeanceLe,
        montantCentimes: ligne.montantCentimes,
        encaisseCentimes: ligne.encaisseCentimes,
        etat: ligne.etat,
        regleeLe: ligne.regleeLe,
      })}
      libelleLigne={(ligne) => ligne.debiteur}
      enEcriture={enEcriture}
      libelleAjouter={t('finances.creances.ajouter')}
      messageVide={t('finances.creances.vide')}
      sansMontant={lignes.filter((ligne) => ligne.montantCentimes === null).length}
      surCreer={creer}
      surModifier={(ligne, changements) => modifier(ligne.id, changements)}
      surSupprimer={(ligne) => supprimer(ligne.id)}
      message={message}
    />
  );
}
